import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { Button } from "./ui/button";
import { Input, Label } from "./ui/input";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Target, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const YEARS = [2022, 2023, 2024, 2025];
const TARGET_YEARS = [2026, 2027, 2028, 2029, 2030, 2035, 2040, 2045, 2050];

export function TargetsDialog({ open, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ kpi_id: "", baseline_year: "2023", target_year: "2030", mode: "pct", pct: "", abs: "", sbti: false });
  const [baseVal, setBaseVal] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/targets").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { if (open) load(); }, [open]);

  useEffect(() => {
    if (!form.kpi_id || !form.baseline_year) return;
    api.get("/targets/baseline-value", { params: { kpi_id: form.kpi_id, year: form.baseline_year } })
      .then((r) => setBaseVal(r.data.value ?? "")).catch(() => setBaseVal(""));
  }, [form.kpi_id, form.baseline_year]);

  const kpi = (data?.kpi_options || []).find((k) => k.id === form.kpi_id);
  const yearsSpan = Number(form.target_year) - Number(form.baseline_year);
  const applyPreset = (p) => {
    const pct = Math.min(90, Math.round(p.annual_pct * yearsSpan * 10) / 10);
    setForm((f) => ({ ...f, mode: "pct", pct: String(pct), sbti: p.sbti_aligned }));
  };
  const computedTarget = () => {
    const b = parseFloat(baseVal);
    if (isNaN(b)) return null;
    if (form.mode === "pct") {
      const p = parseFloat(form.pct);
      if (isNaN(p)) return null;
      return kpi?.lower_is_better === false ? b * (1 + p / 100) : b * (1 - p / 100);
    }
    const a = parseFloat(form.abs);
    return isNaN(a) ? null : a;
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        kpi_id: form.kpi_id, baseline_year: Number(form.baseline_year), target_year: Number(form.target_year),
        baseline_value: baseVal === "" ? null : parseFloat(baseVal), sbti_aligned: form.sbti,
      };
      if (form.mode === "pct") body.target_reduction_pct = parseFloat(form.pct);
      else body.target_value = parseFloat(form.abs);
      await api.post("/targets", body);
      toast.success("Target saved — status chips now track the glide path");
      setForm({ kpi_id: "", baseline_year: "2023", target_year: "2030", mode: "pct", pct: "", abs: "", sbti: false });
      setBaseVal(""); load(); onSaved?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete target '${t.name}'?`)) return;
    try { await api.delete(`/targets/${t.id}`); toast.success("Target deleted"); load(); onSaved?.(); } catch (e) { toast.error(apiErr(e)); }
  };

  const tv = computedTarget();
  const canSave = form.kpi_id && baseVal !== "" && ((form.mode === "pct" && form.pct !== "") || (form.mode === "abs" && form.abs !== ""));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="targets-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Target className="h-4 w-4 text-accent" /> Reduction targets</DialogTitle></DialogHeader>

        <div className="card-flat p-4 space-y-3">
          <div className="label-eyebrow">New target</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Environmental KPI</Label>
              <Select value={form.kpi_id} onValueChange={(v) => setForm((f) => ({ ...f, kpi_id: v }))}>
                <SelectTrigger data-testid="target-kpi-select"><SelectValue placeholder="Choose KPI" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {(data?.kpi_options || []).map((k) => <SelectItem key={k.id} value={k.id}>{k.name} ({k.unit})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Baseline year</Label>
              <Select value={form.baseline_year} onValueChange={(v) => setForm((f) => ({ ...f, baseline_year: v }))}>
                <SelectTrigger data-testid="target-baseline-year"><SelectValue /></SelectTrigger>
                <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>FY{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Baseline value {kpi ? `(${kpi.unit})` : ""}</Label>
              <Input type="number" step="any" value={baseVal} onChange={(e) => setBaseVal(e.target.value)}
                placeholder="Auto-filled from data" data-testid="target-baseline-value" />
            </div>
            <div><Label>Target year</Label>
              <Select value={form.target_year} onValueChange={(v) => setForm((f) => ({ ...f, target_year: v }))}>
                <SelectTrigger data-testid="target-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TARGET_YEARS.map((y) => <SelectItem key={y} value={String(y)}>FY{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="label-eyebrow text-[10px]">Presets</span>
            {(data?.presets || []).map((p) => (
              <Button key={p.id} variant="outline" size="sm" onClick={() => applyPreset(p)} data-testid={`target-preset-${p.id}`}>
                <Sparkles className="h-3 w-3" /> {p.label} (~{p.annual_pct}%/yr)
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Mode</Label>
              <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
                <SelectTrigger data-testid="target-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pct">{kpi?.lower_is_better === false ? "Increase %" : "Reduction %"} vs baseline</SelectItem>
                  <SelectItem value="abs">Absolute target value</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.mode === "pct" ? (
              <div><Label>{kpi?.lower_is_better === false ? "Increase" : "Reduction"} %</Label>
                <Input type="number" step="any" value={form.pct} onChange={(e) => setForm((f) => ({ ...f, pct: e.target.value }))} data-testid="target-pct-input" /></div>
            ) : (
              <div><Label>Target value {kpi ? `(${kpi.unit})` : ""}</Label>
                <Input type="number" step="any" value={form.abs} onChange={(e) => setForm((f) => ({ ...f, abs: e.target.value }))} data-testid="target-abs-input" /></div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground" data-testid="target-preview">
              {tv != null && kpi ? <>Target FY{form.target_year}: <span className="font-mono text-foreground">{formatNum(tv, { decimals: 1 })} {kpi.unit}</span>{form.sbti && <Badge variant="outline" className="ml-2 text-[9px]">SBTi-aligned</Badge>}</> : "Pick a KPI and set the reduction to preview the target."}
            </div>
            <Button variant="primary" disabled={!canSave || busy} onClick={save} data-testid="target-save-btn">{busy ? "Saving…" : "Save target"}</Button>
          </div>
        </div>

        <div className="space-y-2">
          <div className="label-eyebrow">Active targets ({data?.targets?.length || 0})</div>
          {(data?.targets || []).map((t) => (
            <div key={t.id} className="card-flat p-3 flex items-center justify-between gap-3" data-testid="target-row">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{t.name} {t.sbti_aligned && <Badge variant="outline" className="ml-1 text-[9px]">SBTi</Badge>}</div>
                <div className="text-[11px] text-muted-foreground font-mono">
                  FY{t.baseline_year}: {formatNum(t.baseline_value)} → FY{t.target_year}: {formatNum(t.target_value)} {t.unit || ""}{t.target_reduction_pct != null ? ` (${t.target_reduction_pct}%)` : ""}
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => remove(t)} data-testid="target-delete-btn"><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
            </div>
          ))}
          {!data?.targets?.length && <div className="text-xs text-muted-foreground">No targets yet — set one above and the Environment status chips will track it.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
